<script setup lang="ts">
defineOptions({ name: "AppMain" });
import { computed, defineComponent, h, type Component } from "vue";
import { useRoute } from "vue-router";
import { useTagsViewStore } from "@shared/store/modules/tagsView";

const route = useRoute();
const tagsStore = useTagsViewStore();

const cachedNames = computed(() => tagsStore.cachedNames);
/** 单例页签复用同一 component key，切换 id 时保留表单实例（确认前可回显缓存） */
const routeKey = computed(() => (route.meta?.singletonTagPath as string) || route.fullPath);

/**
 * keep-alive include 只认组件 name。动态路由的 name 来自后台 routeName，
 * 与 defineOptions.name 可能不一致（多路由共用同一 .vue、或后台改了名称）。
 * 按页签 cacheName 包一层，保证 include 一定能命中。
 */
const keepAliveWrappers = new Map<string, Component>();

function resolveCacheName(): string {
    const fromMeta = route.meta?.keepAliveName;
    if (typeof fromMeta === "string" && fromMeta) return fromMeta;
    return typeof route.name === "string" ? route.name : "";
}

function wrapKeepAlivePage(component: Component | undefined) {
    const cacheName = resolveCacheName();
    if (!component || !cacheName) return component;
    let wrapper = keepAliveWrappers.get(cacheName);
    if (!wrapper) {
        wrapper = defineComponent({
            name: cacheName,
            setup() {
                return () => h(component);
            },
        });
        keepAliveWrappers.set(cacheName, wrapper);
    }
    return wrapper;
}
</script>

<template>
    <section class="app-main">
        <router-view v-slot="{ Component }">
            <keep-alive :include="cachedNames">
                <component :is="wrapKeepAlivePage(Component)" :key="routeKey" class="app-main-page" />
            </keep-alive>
        </router-view>
    </section>
</template>

<style lang="scss" scoped>
.app-main {
    position: relative;
    width: 100%;
    height: calc(100vh - var(--layout-header-height));
    padding: var(--layout-content-padding);
    overflow: hidden;

    @include scrollbar();
}

.app-main-page {
    height: 100%;
    min-height: 0;
    overflow: auto;

    @include scrollbar();
}

.app-main--page-maximized {
    height: 100vh;
    padding: 0;
    overflow: hidden;
}
</style>
